
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Play, Pause, X, CreditCard, AlertTriangle } from 'lucide-react';
import { Subscription } from '@/services/types/subscriptionTypes';
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge';
import { formatCurrency } from '@/utils/dateUtils';
import { tariffs } from '@/pages/courier/tariff.constants';

interface SubscriptionCardProps {
  subscription: Subscription;
  onStatusChange: (subscriptionId: string, status: 'active' | 'paused' | 'cancelled') => Promise<void>;
  actionLoading: string | null;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ 
  subscription,
  onStatusChange,
  actionLoading
}) => {
  // Определяем, является ли подписка тарифом rent-to-own
  const isRentToOwn = () => {
    // Проверяем соответствие интервала и примерной суммы тарифу rent-to-own
    const rentToOwnTariff = tariffs.find(tariff => tariff.key === 'rent-to-own');
    if (rentToOwnTariff && (subscription.interval === 'month' || subscription.period === 'monthly') && 
        Math.abs(subscription.amount - rentToOwnTariff.price) < 1000) {
      return true;
    }
    return false;
  };
  
  // Определяем детали для отображения
  const bikeModel = isRentToOwn() ? '6 Месяцев' : 'Велосипед'; // Меняем название для rent-to-own
  const startDate = subscription.startDate || subscription.start_date 
    ? new Date(subscription.startDate || subscription.start_date || '').toLocaleDateString('ru-RU') 
    : 'Не указана';
  const nextPaymentDate = subscription.nextPaymentDate || subscription.next_payment_date 
    ? new Date(subscription.nextPaymentDate || subscription.next_payment_date || '').toLocaleDateString('ru-RU')
    : 'Не указана';
  
  const isLoading = actionLoading === subscription.id;
  const amount = subscription.amount || 0;
  
  // Расчет дней до следующего платежа
  const getDaysUntil = (): number | null => {
    const nextDate = subscription.nextPaymentDate || subscription.next_payment_date;
    if (!nextDate) return null;
    
    const now = new Date();
    const paymentDate = new Date(nextDate);
    
    now.setHours(0, 0, 0, 0);
    paymentDate.setHours(0, 0, 0, 0);
    
    const diffTime = paymentDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  
  const daysUntilPayment = getDaysUntil();
  const isPaymentSoon = daysUntilPayment !== null && daysUntilPayment <= 5;
  
  // Определение интервала для отображения
  const getInterval = (): string => {
    if (subscription.period) {
      const periodMap: Record<string, string> = {
        'daily': 'день',
        'weekly': 'неделя',
        'monthly': 'месяц',
        'yearly': 'год'
      };
      return periodMap[subscription.period] || subscription.period;
    } else if (subscription.interval) {
      const intervalMap: Record<string, string> = {
        'day': 'день',
        'week': 'неделя',
        'month': 'месяц',
        'year': 'год'
      };
      return intervalMap[subscription.interval] || subscription.interval;
    }
    return 'месяц'; // По умолчанию
  };
  
  const interval = getInterval();
  
  // Отображение маскированного номера карты - добавляем проверку на null
  // и улучшаем вывод, чтобы явно показать, привязана ли карта
  const cardInfo = (subscription.cardLast4 || subscription.card_last4) 
    ? `•••• ${subscription.cardLast4 || subscription.card_last4}` 
    : 'Карта не привязана';
    
  return (
    <Card className="overflow-hidden border border-border bg-card/30 hover:shadow-md transition-all">
      <CardHeader className="bg-muted/30 pb-4">
        <div className="flex justify-between items-center">
          <CardTitle className="text-lg font-medium">{bikeModel}</CardTitle>
          <SubscriptionStatusBadge status={subscription.status} />
        </div>
      </CardHeader>
      <CardContent className="pt-4 pb-2 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Оплата:</span>
          <span className="font-medium">{formatCurrency(amount)} / {interval}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Дата начала:</span>
          <span>{startDate}</span>
        </div>
        {subscription.status === 'active' && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Следующий платеж:</span>
            {isPaymentSoon ? (
              <span className="flex items-center text-amber-600">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {nextPaymentDate} 
                {daysUntilPayment === 0 ? ' (сегодня)' : daysUntilPayment === 1 ? ' (завтра)' : ` (через ${daysUntilPayment} дн.)`}
              </span>
            ) : (
              <span>{nextPaymentDate}</span>
            )}
          </div>
        )}
        {/* Информация о карте с улучшенным отображением статуса */}
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground flex items-center">
            <CreditCard className="h-3 w-3 mr-1" />Карта:
          </span>
          <span className={subscription.card_last4 ? "text-green-600" : "text-amber-600"}>
            {cardInfo}
          </span>
        </div>
      </CardContent>
      <CardFooter className="pt-2 flex gap-2 justify-end">
        {/* Показываем кнопку активации только для неактивных подписок */}
        {subscription.status !== 'active' && (
          <Button 
            variant="ghost" 
            size="sm"
            className="text-green-600 hover:text-green-700 hover:bg-green-50"
            onClick={() => onStatusChange(subscription.id, 'active')}
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
            Активировать
          </Button>
        )}
        
        {/* ИЗМЕНЕНИЕ: Убираем кнопки приостановки и отмены для активных подписок */}
        {/* Показываем кнопку приостановки только для неактивных подписок */}
        {subscription.status !== 'active' && subscription.status !== 'paused' && subscription.status !== 'cancelled' && (
          <Button 
            variant="ghost" 
            size="sm"
            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            onClick={() => onStatusChange(subscription.id, 'paused')}
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4 mr-1" />}
            Приостановить
          </Button>
        )}
        
        {/* Показываем кнопку отмены только для неактивных подписок */}
        {subscription.status !== 'active' && subscription.status !== 'cancelled' && (
          <Button 
            variant="ghost" 
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => onStatusChange(subscription.id, 'cancelled')}
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4 mr-1" />}
            Отменить
          </Button>
        )}
        
        {/* Добавляем сообщение для активных подписок */}
        {subscription.status === 'active' && (
          <div className="text-xs text-muted-foreground italic">
            Для управления активной подпиской обратитесь к администратору
          </div>
        )}
      </CardFooter>
    </Card>
  );
};
